
// src/pages/FSETracking/FSETrackingPage.js
// Web counterpart of the app's FSE Tracking / Route List / Map screens.
// Admin / Marketing Manager / Marketing Executive see every FSE's sessions;
// an FSE logged in on the web sees only their own — same split RouteList.js
// does on the app (userRole === 'ADMIN' ? all sessions : own sessions only).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Navigation,
  MapPin,
  Timer,
  Route as RouteIcon,
  RefreshCw,
  X,
  CheckCircle,
  Users,
  Search,
} from 'lucide-react';
import {
  fetchAllSessions,
  fetchSessionById,
  rebuildSession,
  clearSelectedSession,
} from '../../services/features/fse/sessionSlice';
import { selectAuthState } from '../../store/selectors/authSelector';
import {
  SectionHeader,
  Button,
  Badge,
  Spinner,
  EmptyState,
  StatCard,
  DataTable,
} from '../../components/ui/UI';
import SessionMap from '../../components/FSETracking/SessionMap';
import './FSETracking.css';

const POLL_INTERVAL_MS = 15000;

// Roles that can see every FSE's sessions. Anyone else (i.e. the FSE role
// itself) only ever sees their own — enforced below, not just hidden in UI.
const ADMIN_VIEW_ROLES = ['Admin', 'MarketingManager', 'MarketingExecutive'];

// ─── helpers ──────────────────────────────────────────────────────────────
const formatDistance = (distKm) => {
  const d = parseFloat(distKm);
  if (!distKm || isNaN(d) || d === 0) return '0 M';
  return d >= 1 ? `${d.toFixed(2)} KM` : `${Math.round(d * 1000)} M`;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

const formatDuration = (start, end) => {
  if (!start) return '—';
  const ms = (end ? new Date(end) : new Date()) - new Date(start);
  const mins = Math.floor(ms / 60000);
  const hrs = Math.floor(mins / 60);
  return hrs > 0 ? `${hrs}h ${mins % 60}m` : `${mins}m`;
};

const fseNameOf = (session, { fseById = {}, selfId, selfName } = {}) => {
  if (typeof session.userId === 'object' && session.userId?.name) return session.userId.name;
  if (session.userName) return session.userName;
  if (session.fseName) return session.fseName;
  const id = typeof session.userId === 'object' ? session.userId?._id : session.userId;
  if (id && selfId && id === selfId && selfName) return selfName;
  if (id && fseById[id]) return fseById[id];
  return id ? `User ${String(id).slice(-6)}` : 'Unknown';
};

const TABS = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'all', label: 'All Time' },
];

const isWithinTab = (isoString, tab) => {
  if (tab === 'all' || !isoString) return true;
  const date = new Date(isoString);
  const now = new Date();
  if (tab === 'today') {
    return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate();
  }
  if (tab === 'week') {
    const weekAgo = new Date(now);
    weekAgo.setDate(now.getDate() - 6);
    weekAgo.setHours(0, 0, 0, 0);
    return date >= weekAgo;
  }
  if (tab === 'month') {
    return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
  }
  return true;
};

const ROLE_LABEL = {
  MarketingManager: 'Manager',
  MarketingExecutive: 'Executive',
  FSE: 'FSE',
  Distributor: 'Distributor',
};

const ROLE_CHIPS = [
  { key: '', label: 'Everyone' },
  { key: 'MarketingManager', label: 'Managers' },
  { key: 'MarketingExecutive', label: 'Executives' },
  { key: 'FSE', label: 'FSE' },
  { key: 'Distributor', label: 'Distributors' },
];

// Who sits BELOW each role (mirrors the backend rules):
//   Admin → Marketing Manager → Marketing Executive
//         → Distributor / Marketing Executive → FSE
// Nobody sees upward or sideways, so a role is never offered a filter for
// people above it.
const ROLES_BELOW = {
  Admin: ['MarketingManager', 'MarketingExecutive', 'Distributor', 'FSE'],
  MarketingManager: ['MarketingExecutive', 'Distributor', 'FSE'],
  MarketingExecutive: ['MarketingExecutive', 'Distributor', 'FSE'],
  Distributor: ['FSE'],
};

// Start of the selected tab as an ISO time — the server filters on it.
const tabStartIso = (tab) => {
  const now = new Date();
  const day = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (tab === 'today') return day.toISOString();
  if (tab === 'week') {
    day.setDate(day.getDate() - 6);
    return day.toISOString();
  }
  if (tab === 'month') return new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
  return undefined; // all time
};

// ─── Session detail panel (map + stats) ────────────────────────────────────
const SessionDetail = ({ sessionId, onClose }) => {
  const dispatch = useDispatch();
  const { selected: session, detailState } = useSelector((s) => s.session);
  const { user } = useSelector(selectAuthState);
  const { list: fseList } = useSelector((s) => s.fse);
  const pollRef = useRef(null);

  const fseById = useMemo(
    () => Object.fromEntries((fseList || []).map((f) => [f._id, f.name])),
    [fseList],
  );
  const nameOpts = { fseById, selfId: user?._id || user?.id, selfName: user?.name };

  useEffect(() => {
    dispatch(fetchSessionById(sessionId));
    return () => dispatch(clearSelectedSession());
  }, [sessionId]);

  const isActive = session && !session.endTime;

  useEffect(() => {
    if (!isActive) return undefined;
    pollRef.current = setInterval(() => {
      dispatch(fetchSessionById(sessionId));
    }, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [isActive, sessionId, dispatch]);

  const handleRefresh = () => dispatch(rebuildSession(sessionId));

  return (
    <div className="fse-track-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="fse-track-panel">
        <div className="fse-track-panel-head">
          <div>
            <h3>{session ? fseNameOf(session, nameOpts) : 'Session'}</h3>
            <span className="fse-track-panel-sub">
              {session ? formatDate(session.startTime) : ''}
              {session?.userRole ? ` · ${ROLE_LABEL[session.userRole] || session.userRole}` : ''}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {isActive && <Badge variant="active" dot>Live</Badge>}
            <button className="fse-track-close" onClick={onClose}><X size={18} /></button>
          </div>
        </div>

        {detailState === 'loading' && !session ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
            <Spinner size="lg" />
          </div>
        ) : !session ? (
          <EmptyState icon={<Navigation size={40} />} title="Couldn't load this session" />
        ) : (
          <>
            <SessionMap route={session.route || []} startLocation={session.startLocation} height={380} />

            <div className="fse-track-stats">
              <div className="fse-track-stat">
                <RouteIcon size={16} />
                <div>
                  <span className="fse-track-stat-val">{formatDistance(session.totalDistanceKm)}</span>
                  <span className="fse-track-stat-lbl">Distance</span>
                </div>
              </div>
              <div className="fse-track-stat">
                <Timer size={16} />
                <div>
                  <span className="fse-track-stat-val">{formatDuration(session.startTime, session.endTime)}</span>
                  <span className="fse-track-stat-lbl">Duration</span>
                </div>
              </div>
              <div className="fse-track-stat">
                <MapPin size={16} />
                <div>
                  <span className="fse-track-stat-val">{session.pointCount ?? session.route?.length ?? 0}</span>
                  <span className="fse-track-stat-lbl">GPS Points</span>
                </div>
              </div>
              <div className="fse-track-stat">
                <CheckCircle size={16} />
                <div>
                  <span className="fse-track-stat-val">{formatTime(session.startTime)} – {session.endTime ? formatTime(session.endTime) : 'Ongoing'}</span>
                  <span className="fse-track-stat-lbl">Start – End</span>
                </div>
              </div>
            </div>

            <div className="fse-track-panel-actions">
              <Button variant="ghost" size="sm" icon={<RefreshCw size={14} />} onClick={handleRefresh} loading={detailState === 'loading'}>
                Refresh Route
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Main page ──────────────────────────────────────────────────────────────
const FSETrackingPage = () => {
  const dispatch = useDispatch();
  const { user, role } = useSelector(selectAuthState);
  const { list: sessions, listState, pagination, loadingMore, stats, people } = useSelector((s) => s.session);
  const { list: fseList } = useSelector((s) => s.fse);

  const isAdmin = role === 'Admin';
  const isAdminView = ADMIN_VIEW_ROLES.includes(role);
  const myUserId = user?._id || user?.id;

  // People this viewer is allowed to see = themselves + everyone below them.
  // (The server decides this from the real registration links.)
  const scopePeople = people || [];
  const hasTeam = isAdmin || scopePeople.some((p) => String(p._id) !== String(myUserId));
  // Team controls (person column, role chips, search…) only when there is a team.
  const showTeamUi = isAdminView && hasTeam;

  const fseById = useMemo(
    () => Object.fromEntries([...(fseList || []), ...scopePeople].map((f) => [f._id, f.name])),
    [fseList, scopePeople],
  );
  const nameOpts = useMemo(
    () => ({ fseById, selfId: myUserId, selfName: user?.name }),
    [fseById, myUserId, user?.name],
  );

  // Role filters: only roles that exist below this viewer
  const roleChips = useMemo(() => {
    const allowed = ROLES_BELOW[role] || [];
    // roles of the OTHER people in scope (you don't need a filter for yourself)
    const present = new Set(
      scopePeople.filter((p) => String(p._id) !== String(myUserId)).map((p) => p.role),
    );
    return ROLE_CHIPS.filter((r) => r.key && allowed.includes(r.key) && present.has(r.key));
  }, [role, scopePeople, myUserId]);

  const [activeTab, setActiveTab] = useState('week');
  const [roleFilter, setRoleFilter] = useState('');
  const [personFilter, setPersonFilter] = useState('');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [activeSessionId, setActiveSessionId] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Everything is filtered on the SERVER: Admin gets every person's sessions,
  // anyone else gets themselves + their own team only.
  const params = useMemo(
    () => ({
      from: tabStartIso(activeTab),
      role: roleFilter || undefined,
      userId: personFilter || undefined,
      search: debounced || undefined,
    }),
    [activeTab, roleFilter, personFilter, debounced],
  );

  const load = useCallback(() => {
    dispatch(fetchAllSessions({ page: 1, limit: 100, ...params }));
  }, [dispatch, params]);

  useEffect(() => {
    load();
  }, [load]);

  // Keep "Live now" fresh without pressing Refresh
  useEffect(() => {
    const t = setInterval(() => {
      if (!document.hidden) load();
    }, 30000);
    return () => clearInterval(t);
  }, [load]);

  // Safety net on the screen as well: never show anyone outside the viewer's
  // own branch of the hierarchy, whatever the server sent.
  const visibleSessions = useMemo(() => {
    const list = sessions || [];
    if (isAdmin) return list;
    const idOf = (s) => (typeof s.userId === 'object' ? s.userId?._id : s.userId);
    if (!isAdminView) return list.filter((s) => String(idOf(s)) === String(myUserId));
    if (scopePeople.length === 0) return list.filter((s) => String(idOf(s)) === String(myUserId));
    const allowed = new Set([...scopePeople.map((p) => String(p._id)), String(myUserId)]);
    return list.filter((s) => allowed.has(String(idOf(s))));
  }, [sessions, isAdmin, isAdminView, myUserId, scopePeople]);

  const rows = useMemo(
    () => [...visibleSessions].sort((a, b) => new Date(b.startTime) - new Date(a.startTime)),
    [visibleSessions],
  );

  // Header numbers: server totals (not limited to the loaded page) when available
  const liveCount = stats?.liveNow ?? rows.filter((s) => !s.endTime).length;
  const todayCount = stats?.sessionsToday ?? rows.filter((s) => isWithinTab(s.startTime, 'today')).length;
  const todayDistance = stats?.distanceTodayKm ?? rows
    .filter((s) => isWithinTab(s.startTime, 'today'))
    .reduce((sum, s) => sum + (parseFloat(s.totalDistanceKm) || 0), 0);
  const peopleCount = stats?.people ?? scopePeople.length;

  const personOptions = (scopePeople.length ? scopePeople : fseList) || [];

  const columns = [
    { key: 'person', label: 'Person', render: (_, row) => (
        <div className="fse-track-row-fse">
          <span className={`fse-track-dot ${!row.endTime ? 'live' : ''}`} />
          <div>
            <div>{fseNameOf(row, nameOpts)}</div>
            {row.userRole && <div className="fse-track-role">{ROLE_LABEL[row.userRole] || row.userRole}</div>}
          </div>
        </div>
      ) },
    { key: 'startTime', label: 'Date', render: (v) => formatDate(v) },
    { key: 'start', label: 'Start', render: (_, row) => formatTime(row.startTime) },
    { key: 'end', label: 'End', render: (_, row) => row.endTime ? formatTime(row.endTime) : <Badge variant="active">Ongoing</Badge> },
    { key: 'duration', label: 'Duration', render: (_, row) => formatDuration(row.startTime, row.endTime) },
    { key: 'totalDistanceKm', label: 'Distance', render: (v) => formatDistance(v) },
    { key: 'pointCount', label: 'Points', render: (v, row) => v ?? row.route?.length ?? 0 },
    { key: 'status', label: 'Status', render: (_, row) => (
        row.endTime
          ? <Badge variant="default">Ended</Badge>
          : <Badge variant="active" dot>Active</Badge>
      ) },
    { key: 'action', label: '', render: (_, row) => (
        <Button size="sm" variant="ghost" icon={<Navigation size={14} />} onClick={() => setActiveSessionId(row._id)}>
          View Route
        </Button>
      ) },
  ];

  // Nobody below you → every row is you, so the name column is redundant.
  const visibleColumns = showTeamUi ? columns : columns.filter((c) => c.key !== 'person');

  const title = isAdmin ? 'Team Tracking' : showTeamUi ? 'My Team Tracking' : 'My Tracking';

  return (
    <div>
      <SectionHeader
        title={title}
        count={rows.length}
        action={
          <Button variant="ghost" size="sm" icon={<RefreshCw size={14} />} onClick={load}>
            Refresh
          </Button>
        }
      />

      <div className="fse-track-stat-grid" style={!showTeamUi ? { gridTemplateColumns: 'repeat(3, 1fr)' } : undefined}>
        <StatCard icon={<Navigation size={20} />} label={showTeamUi ? 'Live Now' : 'Live'} value={liveCount} accent="red" />
        <StatCard icon={<CheckCircle size={20} />} label="Sessions Today" value={todayCount} accent="green" />
        <StatCard icon={<RouteIcon size={20} />} label="Distance Today" value={formatDistance(todayDistance)} accent="blue" />
        {showTeamUi && (
          <StatCard icon={<Users size={20} />} label="People Tracked" value={peopleCount} accent="purple" />
        )}
      </div>

      {/* Role filters — only the roles that sit below this viewer */}
      {showTeamUi && (roleChips.length > 0 || !isAdmin) && (
        <div className="fse-track-tabs fse-track-roles">
          <button
            className={`fse-track-tab ${!roleFilter && !personFilter ? 'active' : ''}`}
            onClick={() => { setRoleFilter(''); setPersonFilter(''); }}
          >
            {isAdmin ? 'Everyone' : 'My team'}
          </button>
          {!isAdmin && (
            <button
              className={`fse-track-tab ${personFilter === String(myUserId) ? 'active' : ''}`}
              onClick={() => { setRoleFilter(''); setPersonFilter(String(myUserId)); }}
            >
              Me
            </button>
          )}
          {roleChips.map((r) => (
            <button
              key={r.key}
              className={`fse-track-tab ${roleFilter === r.key ? 'active' : ''}`}
              onClick={() => { setRoleFilter(r.key); setPersonFilter(''); }}
            >
              {r.label}
            </button>
          ))}
        </div>
      )}

      <div className="fse-track-toolbar">
        <div className="fse-track-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              className={`fse-track-tab ${activeTab === t.key ? 'active' : ''}`}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {showTeamUi && (
          <div className="fse-track-filters">
            <div className="fse-track-search">
              <Search size={14} />
              <input placeholder="Search name…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <select className="fse-track-select" value={personFilter} onChange={(e) => setPersonFilter(e.target.value)}>
              <option value="">{isAdmin ? 'All people' : 'My team'}</option>
              {personOptions
                .filter((p) => !roleFilter || p.role === roleFilter || !p.role)
                .map((p) => (
                  <option key={p._id} value={p._id}>
                    {p.name}{p.role ? ` · ${ROLE_LABEL[p.role] || p.role}` : ''}
                    {String(p._id) === String(myUserId) ? ' (me)' : ''}
                  </option>
                ))}
            </select>
          </div>
        )}
      </div>

      {listState === 'loading' && sessions.length === 0 ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <Spinner size="lg" />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Navigation size={40} />}
          title="No tracking sessions found"
          subtitle={showTeamUi
            ? 'Sessions appear here once you or your team start the day on the app. Try a longer period such as “This Month”.'
            : 'Your sessions will appear here once you start your day on the app.'}
        />
      ) : (
        <>
          <DataTable columns={visibleColumns} data={rows} keyField="_id" disablePagination />
          {showTeamUi && pagination?.page < pagination?.totalPages && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
              <Button
                variant="ghost"
                size="sm"
                loading={loadingMore}
                onClick={() => dispatch(fetchAllSessions({ page: pagination.page + 1, limit: pagination.limit, append: true, ...params }))}
              >
                Load more sessions
              </Button>
            </div>
          )}
        </>
      )}

      {activeSessionId && (
        <SessionDetail sessionId={activeSessionId} onClose={() => setActiveSessionId(null)} />
      )}
    </div>
  );
};

export default FSETrackingPage;

//--------- 13.10.2026 Backup -------------
// // src/pages/FSETracking/FSETrackingPage.js
// // Web counterpart of the app's FSE Tracking / Route List / Map screens.
// // Admin / Marketing Manager / Marketing Executive see every FSE's sessions;
// // an FSE logged in on the web sees only their own — same split RouteList.js
// // does on the app (userRole === 'ADMIN' ? all sessions : own sessions only).
// import React, { useEffect, useMemo, useRef, useState } from 'react';
// import { useDispatch, useSelector } from 'react-redux';
// import {
//   Navigation,
//   MapPin,
//   Timer,
//   Route as RouteIcon,
//   RefreshCw,
//   X,
//   CheckCircle,
//   Users,
//   Search,
// } from 'lucide-react';
// import {
//   fetchAllSessions,
//   fetchSessionById,
//   rebuildSession,
//   clearSelectedSession,
// } from '../../services/features/fse/sessionSlice';
// import { selectAuthState } from '../../store/selectors/authSelector';
// import {
//   SectionHeader,
//   Button,
//   Badge,
//   Spinner,
//   EmptyState,
//   StatCard,
//   DataTable,
// } from '../../components/ui/UI';
// import SessionMap from '../../components/FSETracking/SessionMap';
// import './FSETracking.css';

// const POLL_INTERVAL_MS = 15000;

// // Roles that can see every FSE's sessions. Anyone else (i.e. the FSE role
// // itself) only ever sees their own — enforced below, not just hidden in UI.
// const ADMIN_VIEW_ROLES = ['Admin', 'MarketingManager', 'MarketingExecutive'];

// // ─── helpers ──────────────────────────────────────────────────────────────
// const formatDistance = (distKm) => {
//   const d = parseFloat(distKm);
//   if (!distKm || isNaN(d) || d === 0) return '0 M';
//   return d >= 1 ? `${d.toFixed(2)} KM` : `${Math.round(d * 1000)} M`;
// };

// const formatDate = (iso) => {
//   if (!iso) return '—';
//   return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
// };

// const formatTime = (iso) => {
//   if (!iso) return '—';
//   return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
// };

// const formatDuration = (start, end) => {
//   if (!start) return '—';
//   const ms = (end ? new Date(end) : new Date()) - new Date(start);
//   const mins = Math.floor(ms / 60000);
//   const hrs = Math.floor(mins / 60);
//   return hrs > 0 ? `${hrs}h ${mins % 60}m` : `${mins}m`;
// };

// const fseNameOf = (session, { fseById = {}, selfId, selfName } = {}) => {
//   if (typeof session.userId === 'object' && session.userId?.name) return session.userId.name;
//   if (session.userName) return session.userName;
//   if (session.fseName) return session.fseName;
//   const id = typeof session.userId === 'object' ? session.userId?._id : session.userId;
//   if (id && selfId && id === selfId && selfName) return selfName;
//   if (id && fseById[id]) return fseById[id];
//   return id ? `FSE ${String(id).slice(-6)}` : 'Unknown';
// };

// const TABS = [
//   { key: 'today', label: 'Today' },
//   { key: 'week', label: 'This Week' },
//   { key: 'month', label: 'This Month' },
//   { key: 'all', label: 'All Time' },
// ];

// const isWithinTab = (isoString, tab) => {
//   if (tab === 'all' || !isoString) return true;
//   const date = new Date(isoString);
//   const now = new Date();
//   if (tab === 'today') {
//     return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate();
//   }
//   if (tab === 'week') {
//     const weekAgo = new Date(now);
//     weekAgo.setDate(now.getDate() - 6);
//     weekAgo.setHours(0, 0, 0, 0);
//     return date >= weekAgo;
//   }
//   if (tab === 'month') {
//     return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
//   }
//   return true;
// };

// // ─── Session detail panel (map + stats) ────────────────────────────────────
// const SessionDetail = ({ sessionId, onClose }) => {
//   const dispatch = useDispatch();
//   const { selected: session, detailState } = useSelector((s) => s.session);
//   const { user } = useSelector(selectAuthState);
//   const { list: fseList } = useSelector((s) => s.fse);
//   const pollRef = useRef(null);

//   const fseById = useMemo(
//     () => Object.fromEntries((fseList || []).map((f) => [f._id, f.name])),
//     [fseList],
//   );
//   const nameOpts = { fseById, selfId: user?._id || user?.id, selfName: user?.name };

//   useEffect(() => {
//     dispatch(fetchSessionById(sessionId));
//     return () => dispatch(clearSelectedSession());
//   }, [sessionId]);

//   const isActive = session && !session.endTime;

//   useEffect(() => {
//     if (!isActive) return undefined;
//     pollRef.current = setInterval(() => {
//       dispatch(fetchSessionById(sessionId));
//     }, POLL_INTERVAL_MS);
//     return () => clearInterval(pollRef.current);
//   }, [isActive, sessionId, dispatch]);

//   const handleRefresh = () => dispatch(rebuildSession(sessionId));

//   return (
//     <div className="fse-track-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
//       <div className="fse-track-panel">
//         <div className="fse-track-panel-head">
//           <div>
//             <h3>{session ? fseNameOf(session, nameOpts) : 'Session'}</h3>
//             <span className="fse-track-panel-sub">{session ? formatDate(session.startTime) : ''}</span>
//           </div>
//           <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
//             {isActive && <Badge variant="active" dot>Live</Badge>}
//             <button className="fse-track-close" onClick={onClose}><X size={18} /></button>
//           </div>
//         </div>

//         {detailState === 'loading' && !session ? (
//           <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
//             <Spinner size="lg" />
//           </div>
//         ) : !session ? (
//           <EmptyState icon={<Navigation size={40} />} title="Couldn't load this session" />
//         ) : (
//           <>
//             <SessionMap route={session.route || []} startLocation={session.startLocation} height={380} />

//             <div className="fse-track-stats">
//               <div className="fse-track-stat">
//                 <RouteIcon size={16} />
//                 <div>
//                   <span className="fse-track-stat-val">{formatDistance(session.totalDistanceKm)}</span>
//                   <span className="fse-track-stat-lbl">Distance</span>
//                 </div>
//               </div>
//               <div className="fse-track-stat">
//                 <Timer size={16} />
//                 <div>
//                   <span className="fse-track-stat-val">{formatDuration(session.startTime, session.endTime)}</span>
//                   <span className="fse-track-stat-lbl">Duration</span>
//                 </div>
//               </div>
//               <div className="fse-track-stat">
//                 <MapPin size={16} />
//                 <div>
//                   <span className="fse-track-stat-val">{session.pointCount ?? session.route?.length ?? 0}</span>
//                   <span className="fse-track-stat-lbl">GPS Points</span>
//                 </div>
//               </div>
//               <div className="fse-track-stat">
//                 <CheckCircle size={16} />
//                 <div>
//                   <span className="fse-track-stat-val">{formatTime(session.startTime)} – {session.endTime ? formatTime(session.endTime) : 'Ongoing'}</span>
//                   <span className="fse-track-stat-lbl">Start – End</span>
//                 </div>
//               </div>
//             </div>

//             <div className="fse-track-panel-actions">
//               <Button variant="ghost" size="sm" icon={<RefreshCw size={14} />} onClick={handleRefresh} loading={detailState === 'loading'}>
//                 Refresh Route
//               </Button>
//             </div>
//           </>
//         )}
//       </div>
//     </div>
//   );
// };

// // ─── Main page ──────────────────────────────────────────────────────────────
// const FSETrackingPage = () => {
//   const dispatch = useDispatch();
//   const { user, role } = useSelector(selectAuthState);
//   const { list: sessions, listState, pagination, loadingMore } = useSelector((s) => s.session);
//   const { list: fseList } = useSelector((s) => s.fse);

//   const isAdminView = ADMIN_VIEW_ROLES.includes(role);
//   const myUserId = user?._id || user?.id;
//   const fseById = useMemo(
//     () => Object.fromEntries((fseList || []).map((f) => [f._id, f.name])),
//     [fseList],
//   );
//   const nameOpts = useMemo(
//     () => ({ fseById, selfId: myUserId, selfName: user?.name }),
//     [fseById, myUserId, user?.name],
//   );

//   const [activeTab, setActiveTab] = useState('today');
//   const [search, setSearch] = useState('');
//   const [fseFilter, setFseFilter] = useState('all');
//   const [activeSessionId, setActiveSessionId] = useState(null);

//   useEffect(() => {
//     dispatch(fetchAllSessions({ page: 1, limit: 50 }));
//   }, [dispatch]);

//   // Sessions this user is actually allowed to see — computed before any
//   // other filter so an FSE can never end up viewing someone else's route,
//   // even if fseFilter/search were somehow tampered with.
//   const visibleSessions = useMemo(() => {
//     if (isAdminView) return sessions || [];
//     return (sessions || []).filter((s) => {
//       const sessionUserId = typeof s.userId === 'object' ? s.userId?._id : s.userId;
//       return sessionUserId === myUserId;
//     });
//   }, [sessions, isAdminView, myUserId]);

//   const filtered = useMemo(() => {
//     return visibleSessions
//       .filter((s) => isWithinTab(s.startTime, activeTab))
//       .filter((s) => !isAdminView || fseFilter === 'all' || (typeof s.userId === 'object' ? s.userId?._id : s.userId) === fseFilter)
//       .filter((s) => !search || fseNameOf(s, nameOpts).toLowerCase().includes(search.toLowerCase()))
//       .sort((a, b) => new Date(b.startTime) - new Date(a.startTime));
//   }, [visibleSessions, activeTab, fseFilter, search, isAdminView, nameOpts]);

//   const liveCount = useMemo(() => visibleSessions.filter((s) => !s.endTime).length, [visibleSessions]);
//   const todayCount = useMemo(() => visibleSessions.filter((s) => isWithinTab(s.startTime, 'today')).length, [visibleSessions]);
//   const todayDistance = useMemo(
//     () => visibleSessions
//       .filter((s) => isWithinTab(s.startTime, 'today'))
//       .reduce((sum, s) => sum + (parseFloat(s.totalDistanceKm) || 0), 0),
//     [visibleSessions],
//   );

//   const columns = [
//     { key: 'fse', label: 'FSE', render: (_, row) => (
//         <div className="fse-track-row-fse">
//           <span className={`fse-track-dot ${!row.endTime ? 'live' : ''}`} />
//           {fseNameOf(row, nameOpts)}
//         </div>
//       ) },
//     { key: 'startTime', label: 'Date', render: (v) => formatDate(v) },
//     { key: 'start', label: 'Start', render: (_, row) => formatTime(row.startTime) },
//     { key: 'end', label: 'End', render: (_, row) => row.endTime ? formatTime(row.endTime) : <Badge variant="active">Ongoing</Badge> },
//     { key: 'duration', label: 'Duration', render: (_, row) => formatDuration(row.startTime, row.endTime) },
//     { key: 'totalDistanceKm', label: 'Distance', render: (v) => formatDistance(v) },
//     { key: 'pointCount', label: 'Points', render: (v, row) => v ?? row.route?.length ?? 0 },
//     { key: 'status', label: 'Status', render: (_, row) => (
//         row.endTime
//           ? <Badge variant="default">Ended</Badge>
//           : <Badge variant="active" dot>Active</Badge>
//       ) },
//     { key: 'action', label: '', render: (_, row) => (
//         <Button size="sm" variant="ghost" icon={<Navigation size={14} />} onClick={() => setActiveSessionId(row._id)}>
//           View Route
//         </Button>
//       ) },
//   ];

//   // Hide the FSE column entirely in self-view — every row is the same person.
//   const visibleColumns = isAdminView ? columns : columns.filter((c) => c.key !== 'fse');

//   return (
//     <div>
//       <SectionHeader
//         title={isAdminView ? 'FSE Tracking' : 'My Tracking'}
//         count={filtered.length}
//         action={
//           <Button variant="ghost" size="sm" icon={<RefreshCw size={14} />} onClick={() => dispatch(fetchAllSessions({ page: 1, limit: 50 }))}>
//             Refresh
//           </Button>
//         }
//       />

//       <div className="fse-track-stat-grid" style={!isAdminView ? { gridTemplateColumns: 'repeat(3, 1fr)' } : undefined}>
//         <StatCard icon={<Navigation size={20} />} label={isAdminView ? 'Live Now' : 'Live'} value={liveCount} accent="red" />
//         <StatCard icon={<CheckCircle size={20} />} label="Sessions Today" value={todayCount} accent="green" />
//         <StatCard icon={<RouteIcon size={20} />} label="Distance Today" value={formatDistance(todayDistance)} accent="blue" />
//         {isAdminView && (
//           <StatCard icon={<Users size={20} />} label="Total FSEs" value={fseList?.length || 0} accent="purple" />
//         )}
//       </div>

//       <div className="fse-track-toolbar">
//         <div className="fse-track-tabs">
//           {TABS.map((t) => (
//             <button
//               key={t.key}
//               className={`fse-track-tab ${activeTab === t.key ? 'active' : ''}`}
//               onClick={() => setActiveTab(t.key)}
//             >
//               {t.label}
//             </button>
//           ))}
//         </div>

//         {isAdminView && (
//           <div className="fse-track-filters">
//             <div className="fse-track-search">
//               <Search size={14} />
//               <input placeholder="Search FSE…" value={search} onChange={(e) => setSearch(e.target.value)} />
//             </div>
//             <select className="fse-track-select" value={fseFilter} onChange={(e) => setFseFilter(e.target.value)}>
//               <option value="all">All FSEs</option>
//               {(fseList || []).map((f) => (
//                 <option key={f._id} value={f._id}>{f.name}</option>
//               ))}
//             </select>
//           </div>
//         )}
//       </div>

//       {listState === 'loading' && sessions.length === 0 ? (
//         <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
//           <Spinner size="lg" />
//         </div>
//       ) : filtered.length === 0 ? (
//         <EmptyState
//           icon={<Navigation size={40} />}
//           title="No tracking sessions found"
//           subtitle={isAdminView
//             ? 'Sessions will appear here once FSEs start their day on the app.'
//             : 'Your sessions will appear here once you start your day on the app.'}
//         />
//       ) : (
//         <>
//           <DataTable columns={visibleColumns} data={filtered} keyField="_id" disablePagination />
//           {isAdminView && pagination?.page < pagination?.totalPages && (
//             <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
//               <Button
//                 variant="ghost"
//                 size="sm"
//                 loading={loadingMore}
//                 onClick={() => dispatch(fetchAllSessions({ page: pagination.page + 1, limit: pagination.limit, append: true }))}
//               >
//                 Load more sessions
//               </Button>
//             </div>
//           )}
//         </>
//       )}

//       {activeSessionId && (
//         <SessionDetail sessionId={activeSessionId} onClose={() => setActiveSessionId(null)} />
//       )}
//     </div>
//   );
// };

// export default FSETrackingPage;